<template>
  <div class="input-matrix-radio-advanced">
    <div>
      <div/>
      <div class="choicegroup" v-for="(choicegroup, cgidx) in choices" :key="`heading-choicegroup-${cgidx}`">
        <span v-if="choicegroup.label">{{ choicegroup.label }}
          <template v-if="choicegroup.for_state">
            <br class="iconbreak"/><Icon name="fieldsState" />
          </template>
        </span>
        <ul>
          <li v-for="(choice, cidx) in choicegroup.choices" :key="`heading-choicegroup-${cgidx}-choice-${cidx}`">
            <span>{{ choice.label }}</span>
          </li>
        </ul>
      </div>
    </div>
    <div>
      <div
        v-for="(item, itemIndex) in ivalue"
        :key="`item-${itemIndex}`"
      >
        <div class="label">
          {{ item.label }}
          <Tooltip
            v-if="item.info"
            icon="info"
            :content="item.info"
            :for-state="forState"
            :for-report="forReport"
          />
        </div>
        <div class="choices" v-for="(choicegroup, cgidx) in choices" :key="`choicegroup-${cgidx}`">
          <template v-if="choicegroup.type === 'radiogroup'">
            <Radio
              v-for="(choice, cidx) in choicegroup.choices"
              :key="`choicegroup-${cgidx}-choice-${cidx}`"
              :value="choice.value"
              :checked.sync="ivalue[itemIndex].value[cgidx]"
              :class="{ changed: ichanges[itemIndex] && ichanges[itemIndex][cgidx] === choice.value && ichanges[itemIndex][cgidx] !== ivalue[itemIndex].value[cgidx] }"
              @input="handleInput"
            />
          </template>
          <template v-else-if="choicegroup.type === 'checkbox'">
            <Checkbox
              v-for="(choice, cidx) in choicegroup.choices"
              :key="`choicegroup-${cgidx}-checkbox-${cidx}`"
              :value="choice.value"
              :checked.sync="ivalue[itemIndex].value[cgidx]"
              @input="handleInput"
              :class="{ changed: ichanges[itemIndex] && ichanges[itemIndex][cgidx] === choice.value && ichanges[itemIndex][cgidx] !== ivalue[itemIndex].value[cgidx] }"
            />
          </template>
        </div>
      </div>
    </div>
    <template v-if="reviewMode">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import _ from 'lodash';
  import Radio from '@/components/ui/inputs/Radio';
  import Tooltip from '@/components/ui/Tooltip';
  import TextInput from '@/components/ui/inputs/TextInput';
  import RadioGroup from '@/components/ui/inputs/groups/RadioGroup';
  import Checkbox from '@/components/ui/inputs/Checkbox';
  import Icon from '@/components/ui/Icon';

  export default {
    name: 'RadioMatrixAdvanced',
    components: {
      Icon,
      Checkbox,
      RadioGroup,
      TextInput,
      Tooltip,
      Radio,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      choices: {
        type: Array,
        default: () => [],
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      reviewMode: {
        type: Boolean,
        default: false,
      },
      reviewId: {
        type: String,
        default: null,
      },
      reviewData: {
        type: Object,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        ivalue: [],
        ichanges: [],
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {},
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value: {
        handler(val) {
          (val || []).forEach((v) => {
            if (!v.value) {
              // eslint-disable-next-line no-param-reassign
              v.value = [];
            }
          });
          this.ivalue = val;
          if (this.reviewMode) {
            this.parseChanges();
          }
        },
        immediate: true,
        deep: true,
      },
      reviewMode(bool) {
        if (bool === false) {
          this.ichanges = [];
        }
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    created() {
      this.ivalue = JSON.parse(JSON.stringify(this.value || []));
      if (this.reviewMode) {
        this.parseChanges();
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      addItem() {
        this.ivalue.push({ label: null, dynamic: true, value: [] });
      },
      removeItem(idx) {
        const ivalue = this.ivalue.slice();
        ivalue.splice(idx, 1);
        this.ivalue = ivalue;
      },
      //----------------------------------
      // Review Methods
      //----------------------------------
      parseChanges() {
        const reviewId = this.reviewId;
        const reviewData = JSON.parse(JSON.stringify(this.reviewData));
        if (reviewData && reviewId) {
          const ivalue = JSON.parse(JSON.stringify(this.ivalue));
          const review = reviewData[reviewId];

          const ichanges = [];
          ivalue.forEach((entry, idx) => {
            const originalValue = entry.value;
            const reviewValue = review[idx].value;

            ichanges[idx] = null;

            if (!_.isEqual(originalValue, reviewValue)) {
              ichanges[idx] = originalValue;
              // eslint-disable-next-line no-param-reassign
              entry.value = reviewValue;
            }
          });

          this.ivalue = ivalue;
          this.ichanges = ichanges;
        }
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleInput() {
        this.$nextTick(() => {
          if (!this.reviewMode) {
            const value = JSON.parse(JSON.stringify(this.ivalue));
            this.$emit('update:value', value);
            this.$emit('input', value);
          }
        });
      },
      handleAdopt(accept) {
        this.$emit('adopt', { accept, value: this.ivalue });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-matrix-radio-advanced {
  & > div {
    &:first-of-type {
      display: flex;
      justify-content: flex-end;
      width: 55vw;
      max-width: 808px;
      padding: 0 16px;

      & > .choicegroup {
        position: relative;
        padding-top: 100px;
        margin-right: 84px;
        text-align: center;
        &:last-of-type { margin-right: 0; }

        & > span {
          @include font-body-small-bold;
          color: $color-black-alpha-60;
          position: absolute;
          display: inline-block;
          bottom: 80%;
          min-width: 70px;
          left: 50%;
          transform: translate(-50%, 0);
          text-align: center;

          & > svg {
            display: inline-block;
            position: relative;
            top: 5px;
            width: 14px;
            height: 14px;
          }
        }


        ul {
          display: flex;
          justify-content: flex-end;
          list-style-type: none;
          margin: 0;
          padding: 0;

          &:last-of-type { margin-right: 0; }

          li {
            position: relative;
            padding: 0;
            margin: 0;
            width: 16px;
            height: 22px;
            margin-right: 48px;
            &:last-of-type { margin-right: 0; }

            & > span {
              top: 0;
              left: 0;
              display: block;
              white-space: nowrap;
              position: absolute;
              transform: rotate(-30deg);
              transform-origin: 0 0;
              @include font-body-small;
              user-select: none;
              pointer-events: none;
              color: $color-black-alpha-80;
            }
          }
        }
      }
    }

    &:last-of-type {
      border: 1px solid $color-black-solid-05;
      width: 55vw;
      max-width: 808px;

      & > div {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        background-color: transparent;
        will-change: background-color;
        transition: background-color $default-transition;
        padding: 12px 16px;

        &:hover {
          background-color: $color-blue-alpha-10;
          &.action { background-color: transparent; }
        }

        & > .label {
          width: 100%;
          @include font-body-medium;
          color: $color-black-solid-100;

          & > .comp-tooltip {
            margin-left: 8px;
          }
        }

        & > .choices {
          display: inline-flex;
          justify-content: flex-end;
          margin-right: 84px;

          &:last-of-type {
            margin-right: 0;
          }

          & > .input-radio {
            margin-right: 48px;
            &:last-of-type {
              margin-right: 0;
            }
          }
        }
      }
    }
  }

  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 16px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }
}
</style>
